import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  CorrectionActorKind,
  CorrectionField,
  MemoryProposalKind,
  MemoryProposalStatus,
  MemoryRuleKind,
  MemoryRuleStatus,
} from '@erp/shared-types';

/** Page size for every harness list; the pagination control reads the same value. */
export const HARNESS_PAGE_SIZE = 25;
const PAGE_SIZE = HARNESS_PAGE_SIZE;

/**
 * The offset that is still on a page when the list holds `total` rows: a
 * queue that shrinks under the reviewer (accept, reject, retire) steps back
 * to its last page instead of showing an empty one.
 */
export function clampOffset(offset: number, total: number): number {
  if (total <= 0) return 0;
  const lastPage =
    Math.floor((total - 1) / HARNESS_PAGE_SIZE) * HARNESS_PAGE_SIZE;
  return Math.min(offset, lastPage);
}

export function useMemoryRules(input: {
  kind?: MemoryRuleKind;
  customerId?: string;
  status?: MemoryRuleStatus;
  search?: string;
  offset?: number;
}) {
  return useQuery({
    ...orpc.harness.rules.list.queryOptions({
      input: { ...input, limit: PAGE_SIZE },
    }),
    placeholderData: (previous) => previous,
  });
}

/** Sibling organizations a sender route may target; fetched only while needed. */
export function useRouteTargets(enabled: boolean) {
  return useQuery({
    ...orpc.harness.rules.routeTargets.queryOptions({}),
    enabled,
  });
}

export function useMemoryRuleUsages(ruleId: string | null, offset = 0) {
  return useQuery({
    ...orpc.harness.rules.getUsages.queryOptions({
      input: { ruleId: ruleId ?? '', limit: PAGE_SIZE, offset },
    }),
    enabled: !!ruleId,
  });
}

export function useMemoryProposals(input: {
  status?: MemoryProposalStatus;
  kind?: MemoryProposalKind;
  customerId?: string;
  draftSalesOrderId?: string;
  offset?: number;
}) {
  return useQuery({
    ...orpc.harness.proposals.list.queryOptions({
      input: { ...input, limit: PAGE_SIZE },
    }),
    refetchInterval: 15_000,
    placeholderData: (previous) => previous,
  });
}

export function useMemoryProposalSummary() {
  return useQuery({
    ...orpc.harness.proposals.getSummary.queryOptions({}),
    refetchInterval: 30_000,
  });
}

export function useCorrectionEvents(input: {
  field?: CorrectionField;
  draftSalesOrderId?: string;
  actorKind?: CorrectionActorKind;
  from?: string;
  to?: string;
  offset?: number;
}) {
  return useQuery({
    ...orpc.harness.corrections.list.queryOptions({
      input: { ...input, limit: PAGE_SIZE },
    }),
    placeholderData: (previous) => previous,
  });
}

export function useDraftInfluence(draftSalesOrderId: string | undefined) {
  return useQuery({
    ...orpc.harness.drafts.getInfluence.queryOptions({
      input: { draftSalesOrderId: draftSalesOrderId ?? '' },
    }),
    enabled: !!draftSalesOrderId,
    staleTime: 30_000,
  });
}

/**
 * Mutations over the harness contract. Accepting a proposal changes rules,
 * the summary and any draft's influence, so the whole namespace is
 * invalidated; a draft's activity timeline is refreshed when one is given.
 */
/** The Brain page: stats, growth, knowledge graph and recent learning for the session org. */
export function useBrainSnapshot() {
  return useQuery({
    ...orpc.harness.brain.get.queryOptions({}),
    placeholderData: (previous) => previous,
  });
}

export function useHarnessActions(draftSalesOrderId?: string) {
  const queryClient = useQueryClient();
  const onSuccess = async () => {
    const invalidations = [
      queryClient.invalidateQueries({ queryKey: orpc.harness.key() }),
    ];
    if (draftSalesOrderId) {
      invalidations.push(
        queryClient.invalidateQueries({
          queryKey:
            orpc.accountsReceivable.draftSalesOrders.getActivity.queryKey({
              input: { draftSalesOrderId },
            }),
        }),
      );
    }
    await Promise.all(invalidations);
  };
  const createProposal = useMutation({
    mutationFn: rpcClient.harness.proposals.create,
    onSuccess,
  });
  const acceptProposal = useMutation({
    mutationFn: rpcClient.harness.proposals.accept,
    onSuccess,
  });
  const rejectProposal = useMutation({
    mutationFn: rpcClient.harness.proposals.reject,
    onSuccess,
  });
  const retireRule = useMutation({
    mutationFn: rpcClient.harness.rules.retire,
    onSuccess,
  });
  const updateRule = useMutation({
    mutationFn: rpcClient.harness.rules.update,
    onSuccess,
  });
  const bootstrapBrain = useMutation({
    mutationFn: rpcClient.harness.brain.bootstrap,
    onSuccess,
  });
  return {
    createProposal,
    acceptProposal,
    rejectProposal,
    retireRule,
    updateRule,
    bootstrapBrain,
  };
}
